<template>
    <TransitionModal>
        <Fixed
            v-if="isOpen"
            @close="close"
        >
            <ModalWithHeader
                :label="title"
                @close="close"
            >
                <AuthForm
                    :start-with-registration
                    :registration-errors="registrationErrors"
                    :registration-disabled
                    :registration-hide-inputs-if-logged
                    @register="$emit('register', $event)"
                    @login="$emit('login', $event)"
                    @login-error="$emit('loginError', $event)"
                >
                    <template #registration-title>
                        {{ title }}
                    </template>

                    <template
                        v-if="content?.description"
                        #registration-description
                    >
                        <!-- eslint-disable vue/no-v-html -->
                        <div
                            class="enquiry-form-cms-content"
                            v-html="localizeHrefs(content.description)"
                        ></div>
                    </template>

                    <template #registration-contact>
                        <slot name="registration-contact" />
                    </template>

                    <template #registration-submit>
                        {{ content?.buttonText ?? t('enquiry.form.submit') }}
                    </template>

                    <template #login-title>
                        {{ title }}
                    </template>
                </AuthForm>
            </ModalWithHeader>
        </Fixed>
    </TransitionModal>
</template>

<script setup lang="ts">
import type { ErrorCollectionWithTraces } from '@/helpers/errors'
import type {
    EnquiryFormContentFragment,
    LoginResponseFailureFragment,
    LoginResponseSuccessFragment,
} from '@/graphql/api'

import AuthForm from './AuthForm.vue'
import TransitionModal from '@/components/layout/modal/TransitionModal.vue'
import Fixed from '@/components/layout/modal/Fixed.vue'
import ModalWithHeader from '@/components/layout/modal/ModalWithHeader.vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHrefLocalizer } from '@/router/href-localizer.ts'

const localizeHrefs = useHrefLocalizer()
const { t } = useI18n()
const { startWithRegistration = true, content } = defineProps<{
    isOpen: boolean
    startWithRegistration?: boolean
    registrationErrors: ErrorCollectionWithTraces
    registrationDisabled?: boolean
    registrationHideInputsIfLogged?: boolean
    content: EnquiryFormContentFragment | undefined
}>()

const title = computed(() => content?.title ?? t('enquiry.form.title'))

const emit = defineEmits<{
    close: []
    login: [v: LoginResponseSuccessFragment]
    register: [successCallback: () => void]
    loginError: [v: LoginResponseFailureFragment]
}>()

const close = () => {
    emit('close')
}
</script>

<style lang="scss">
.unscoped-enquiry-form-cms-content {
    font-size: 1rem;
    line-height: 1.2em;
    font-family: $sectra-font-stack;

    strong,
    b {
        font-weight: bold;
    }

    em,
    i {
        font-style: italic;
    }

    p {
        font-size: 1.125rem;
    }

    ul,
    ol {
        background-color: $sage;
        border-radius: var(--input-border-radius);
        padding: var(--spacing-16) var(--spacing-20i) var(--spacing-16)
            var(--spacing-40);
        font-size: 1rem;
        max-width: 340px;
        margin: var(--spacing-16) auto;
        text-align: left;
    }

    ul {
        list-style: disc outside;
    }

    ol {
        list-style: decimal outside;
    }
}
</style>
